import React, { useEffect, useState } from 'react'
import {
	Box,
	CircularProgress,
	Container,
	Typography,
	Divider,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Checkbox,
	FormControlLabel,
	FormGroup,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel, Chip
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import {useUserStore} from "../../state"
import {doGet} from "../../util/do-fetch"
import getSupabase from "../../util/supabase-utils"

const ActiveMediaJobs = () => {
	const [mediaJobs, setMediaJobs] = useState([])
	const [statusFilters, setStatusFilters] = useState({})
	const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
	const [loading, setLoading] = useState(true)
	const [categories, setCategories] = useState([])
	const [selectedCategory, setSelectedCategory] = useState('All')
	const navigate = useNavigate()
	const { user } = useUserStore()

	useEffect(() => {
		if (user) {
			const fetchMediaJobs = async () => {
				setLoading(true)
				const response = await doGet('/admin/active-media-jobs')
				const data = await response.json()
				setMediaJobs(data)

				const uniqueStatuses = [...new Set(data.map(job => job.status))]
				const initialStatusFilters = {}
				uniqueStatuses.forEach(status => {
					initialStatusFilters[status] = true
				})
				setStatusFilters(initialStatusFilters)
				setLoading(false)
			}

			const fetchCategories = async () => {
				const { data, error } = await getSupabase()
					.from('category')
					.select('*')
					.order('name', { ascending: true })

				if (error) {
					console.error('Error fetching categories:', error.message)
				} else {
					setCategories(data)
				}
			}

			fetchMediaJobs()
			fetchCategories()
		}
	}, [user])

	const formatStatus = (status) => {
		return status
			.split('-')
			.map(s => s.charAt(0).toUpperCase() + s.slice(1))
			.join(' ')
	}

	const handleStatusToggle = (status) => {
		setStatusFilters(prev => ({
			...prev,
			[status]: !prev[status]
		}))
	}

	const filteredJobs = mediaJobs.filter(job => {
		const statusMatch = statusFilters[job.status]
		const categoryMatch = selectedCategory === 'All' || job.category_id === selectedCategory
		return statusMatch && categoryMatch
	})

	const handleOpenError = (message) => {
		setErrorDialog({ open: true, message })
	}

	const handleCloseError = () => {
		setErrorDialog({ open: false, message: '' })
	}

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" mt={4}>
				<CircularProgress />
			</Box>
		)
	}

	return (
		<Container maxWidth="lg">
			<Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
				<Typography variant="h5">Active Media Jobs</Typography>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel>Category</InputLabel>
					<Select
						value={selectedCategory}
						label="Category"
						onChange={(e) => setSelectedCategory(e.target.value)}
					>
						<MenuItem value="All">All</MenuItem>
						{categories.map(cat => (
							<MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			<Divider sx={{ my: 2 }} />
			<FormGroup row sx={{ mb: 2 }}>
				{Object.keys(statusFilters).map(status => (
					<FormControlLabel
						key={status}
						control={<Checkbox checked={statusFilters[status]} onChange={() => handleStatusToggle(status)} />}
						label={formatStatus(status)}
					/>
				))}
			</FormGroup>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Product & Category</TableCell>
						<TableCell>Status</TableCell>
						<TableCell>Voice / Model</TableCell>
						<TableCell>Paused At</TableCell>
						<TableCell>Failed At</TableCell>
						<TableCell>Status Updated</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{filteredJobs.map(job => (
						<TableRow
							key={job.id}
							hover
							style={{ cursor: 'pointer' }}
							onClick={() => navigate(`/admin/products/${job.product_id}/media/${job.id}`)}
						>
							<TableCell>
								<Typography variant="body1">{job.product_name}</Typography>
								<Typography variant="body2" style={{ color: job.category_color }}>
									{job.category_name}
									{
										job.environment !== 'prod' &&
										<Chip sx={{ml: 1}} size={"small"} variant={"outlined"} color={"warning"} label={job.environment} />
									}
								</Typography>
							</TableCell>
							<TableCell>{formatStatus(job.status)}</TableCell>
							<TableCell>{`${job.voice_id} / ${job.tts_model}`}</TableCell>
							<TableCell>{job.paused_at ? dayjs(job.paused_at).format('MMM D, YYYY h:mm A') : '—'}</TableCell>
							<TableCell>
								{job.failed_at ? (
									<>
										<Typography display="inline">{dayjs(job.failed_at).format('MMM D, YYYY h:mm A')}</Typography>
										{job.message && (
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenError(job.message) }}>
												<ErrorOutlineIcon color="error" fontSize="small" />
											</IconButton>
										)}
									</>
								) : '—'}
							</TableCell>
							<TableCell>{job.status_updated_at ? dayjs(job.status_updated_at).format('MMM D, YYYY h:mm A') : '—'}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<Dialog open={errorDialog.open} onClose={handleCloseError}>
				<DialogTitle>Error Message</DialogTitle>
				<DialogContent>
					<DialogContentText>{errorDialog.message}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseError} color="primary">Close</Button>
				</DialogActions>
			</Dialog>
		</Container>
	)
}

export default ActiveMediaJobs
